import
React, {
    Suspense,
    useEffect,
    useMemo
} from "react";
import {
    BrowserRouter,
    Navigate,
    Route,
    Routes
} from "react-router-dom";

import ReactDOM from "react-dom/client";

import * as amplitude from "@amplitude/analytics-browser";
import { prepareAmplitudeSessionTransfer, sessionTransferPlugin } from "./analytics/services/sessionTransfer";
import { GrowthBook, GrowthBookProvider } from "@growthbook/growthbook-react";

import { APP_VERSION } from "./constants/version";

import { useVersionCheck } from "./hooks/AppVersionCheck/useVersionCheck";
import useUtmToStorage from "./hooks/useUtmToStorage";

import { GrowthBookABtestProvider } from "./context/GrowthBookContext";
// import { FirebaseConfigProvider } from "./context/RemoteConfigContext";
import { ConfigLoaderProvider } from "./context/ConfigLoaderContext";
import { ErrorHandlerProvider } from "./context/ErrorHandlerContext";
import { RouteHandlerProvider } from "./context/RouteHandlerContext";
import { AppConfigProvider } from "./context/AppConfig/AppConfigContext";
import { UserDataProvider } from "./context/UserData/UserDataContext";
import { DevModeProvider } from "./context/DevMode";
import { DeviceProvider } from "./context/DeviceCheckContext";
import { EnvProvider } from "./context/EnvironmentContext";
import { UrlProvider } from "./context/urlContext";
import FlowAvailabilityGate from "./components/FlowAvailabilityGate/FlowAvailabilityGate";


import { AnalyticsUserDataProvider } from "./analytics/context/AnalyticsUserData";
import { CampaignswellProvider } from "./analytics/context/CampaignswellContext";
import { EventFilterProvider } from "./analytics/context/EventFilter";
import { AmplitudeProvider } from "./analytics/context/AmplitudeContext";
import { EventProvider } from "./analytics/context/EventContext";


import Loader from "./templates/Loader/Loader"

import "./index.css";
import { initializeCSSVariables } from "./themes/cssVariables";
import { hasQaLogParam } from "./utils/urlParams";

const MainNewT = React.lazy(() => import('./flows/NewT/MainNewT'));

const MainNewT2 = React.lazy(() => import('./flows/NewT2/MainNewT2'));

const MainNewH = React.lazy(() => import('./flows/NewH/MainNewH'));

const MainHp = React.lazy(() => import('./flows/HolidayPromo/flows/Hp/MainHp'));
const MainHp3 = React.lazy(() => import('./flows/HolidayPromo/flows/Hp3/MainHp3'));
const MainHp23D = React.lazy(() => import('./flows/HolidayPromo/flows/Hp23d/MainHp23D'));

const MainNewH2 = React.lazy(() => import('./flows/NewH2/MainNewH2'));

const MainNewS = React.lazy(() => import('./flows/NewS/MainNewS'));

const MainIqTest = React.lazy(() => import(`./flows/IqTest/MainIqTest`))

const ApplyDiscount = React.lazy(() => import('./flows/ApplyDiscount/MainApplyDiscount'));

const MainLearningDeskOne = React.lazy(() => import('./flows/LearningDeskOne/MainLearningDeskOne'));
const MainW2A = React.lazy(() => import('./flows/W2A/MainW2A'));


function VersionCheckWrapper({ children, version }) {
    const loaderOrStatus = useVersionCheck(version);

    if (React.isValidElement(loaderOrStatus)) {
        return loaderOrStatus;
    }

    if (loaderOrStatus) {
        return <Loader />;
    }

    return children;
}

const amplitudeSessionTransfer = prepareAmplitudeSessionTransfer();
amplitude.add(sessionTransferPlugin());
amplitude.init("ee6a72438081ce5583b67e41c270dbd", {
    ...amplitudeSessionTransfer,
    defaultTracking: true,
    autocapture: {
        attribution: true,
        sessions: true,
        formInteractions: true,
    },
});

const getOrCreateUserId = () => {
    const storedId = localStorage.getItem("MemoryOSabid");
    if (storedId) {
        return storedId;
    }
    const newId = crypto.randomUUID();
    localStorage.setItem("MemoryOSabid", newId);
    return newId;
};

const growthbook = new GrowthBook({
    apiHost: "https://cdn.growthbook.io",
    clientKey: "sdk-42gJQRjfigHm1xSc",
    enableDevMode: true,
    subscribeToChanges: true,
    attributes: {
        id: getOrCreateUserId(),
        deviceId: navigator.userAgent,
    },
    trackingCallback: (experiment, result) => {
        // TODO: Use your real analytics tracking system
        // console.log("Viewed Experiment", {
        //   experimentId: experiment.key,
        //   variationId: result.key,
        // });
    },
});

const Index = () => {
    useUtmToStorage();
    const environment = useMemo(() => {
        const currentURL = window.location.href;
        const productionDomain = "start.memoryos.com";
        const stagingDomain = "staging.start.memoryos.com";

        if (
            currentURL.includes(productionDomain) &&
            !currentURL.includes(stagingDomain)
        ) {
            return "production";
        } else if (
            currentURL.includes("localhost") ||
            currentURL.includes("127.0.0.1")
        ) {
            return "development";
        } else {
            return "staging";
        }
    }, []);

    useEffect(() => {
        if (environment === "staging" || hasQaLogParam()) {
            import("eruda").then((lib) => lib.default.init());
        }
    }, [environment]);

    useEffect(() => {
        initializeCSSVariables();
    }, []);

    useEffect(() => {
        growthbook.loadFeatures();
    }, []);

    return (
        <GrowthBookProvider growthbook={growthbook}>
            <EnvProvider>
                <RouteHandlerProvider>
                    <BrowserRouter>
                        <FlowAvailabilityGate>
                            <UrlProvider>
                                <GrowthBookABtestProvider>
                                    <ErrorHandlerProvider>
                                        <DeviceProvider>
                                            <DevModeProvider>
                                                <ConfigLoaderProvider>
                                                    <AppConfigProvider>
                                                        <UserDataProvider>
                                                            <EventFilterProvider>
                                                                <CampaignswellProvider>
                                                                    <EventProvider>
                                                                        <AnalyticsUserDataProvider>

                                                                            <AmplitudeProvider>
                                                                                <Suspense fallback={<Loader />}>
                                                                                    <Routes>

                                                                                        <Route path="/iq-test/*"
                                                                                            element={<MainIqTest />} />

                                                                                        <Route path="/new-t/*"
                                                                                            element={<MainNewT />} />

                                                                                        <Route path="/new-t2/*"
                                                                                            element={<MainNewT2 />} />

                                                                                        <Route path="/new-h/*"
                                                                                            element={<MainNewH />} />

                                                                                        <Route path="/hp/*" element={<MainHp />} />
                                                                                        <Route path="/hp-3/*" element={<MainHp3 />} />
                                                                                        <Route path="/hp-23d/*" element={<MainHp23D />} />

                                                                                        <Route path="/new-h2/*"
                                                                                            element={<MainNewH2 />} />

                                                                                        <Route path="/new-s/*"
                                                                                            element={<MainNewS />} />

                                                                                        <Route path="/apply-discount/*"
                                                                                            element={<ApplyDiscount />} />

                                                                                        <Route path="/learning-desk-1/*"
                                                                                            element={<MainLearningDeskOne />} />

                                                                                        <Route path="/w2a/*"
                                                                                            element={<MainW2A />} />

                                                                                        {/* Default redirect */}
                                                                                        <Route path="/" element={<Navigate
                                                                                            to="/new-h2" replace />} />
                                                                                        <Route path="*" element={<Navigate
                                                                                            to="/new-h2" replace />} />
                                                                                    </Routes>
                                                                                </Suspense>
                                                                            </AmplitudeProvider>

                                                                        </AnalyticsUserDataProvider>
                                                                    </EventProvider>
                                                                </CampaignswellProvider>
                                                            </EventFilterProvider>
                                                        </UserDataProvider>
                                                    </AppConfigProvider>
                                                </ConfigLoaderProvider>
                                            </DevModeProvider>
                                        </DeviceProvider>
                                    </ErrorHandlerProvider>
                                </GrowthBookABtestProvider>
                            </UrlProvider>
                        </FlowAvailabilityGate>
                    </BrowserRouter>
                </RouteHandlerProvider>
            </EnvProvider>
        </GrowthBookProvider>
    );
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
    <VersionCheckWrapper version={APP_VERSION.CURRENT}>
        <Index />
    </VersionCheckWrapper>
);
